import { Box } from "@mui/material";
import type { SalesOrgLeadCard, SalesOrgLeadStatus } from "@xop/dashboard-shared";
import type { JSX } from "react";

import { toneChipSx, type HeatPillDisplay, type SxStyle } from "../../leads/heatStyles.js";
import { verdictMeta } from "../../leads/quality.js";

/* ── Cells / pills ─────────────────────────────────────────────── */

export function HeatPill({ display }: { display: HeatPillDisplay }): JSX.Element {
  return (
    <Box
      component="span"
      {...(display.title ? { title: display.title } : {})}
      sx={[
        {
          display: "inline-flex",
          alignItems: "center",
          borderRadius: 999,
          px: 1,
          py: 0.25,
          fontSize: 10,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          whiteSpace: "nowrap",
        },
        display.sx,
      ]}
    >
      {display.label}
    </Box>
  );
}

export function ConfidenceCell({ pct }: { pct: number }): JSX.Element {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        aria-hidden
        component="span"
        sx={{ display: "inline-block", height: 4, width: 48, overflow: "hidden", borderRadius: 999, bgcolor: "surfaceSoft" }}
      >
        <Box component="span" sx={{ display: "block", height: "100%", borderRadius: 999, bgcolor: "primary.main", width: `${pct}%` }} />
      </Box>
      <Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary" }}>
        {pct}%
      </Box>
    </Box>
  );
}

/**
 * "Existing customer" — is this org already an XOP customer? Derived from
 * `converted_at`, which the agent's CRM-customer match sweep stamps when the
 * lead's organisation already exists in the CRM as a `kind="customer"` record.
 * "Yes" is emphasised (success tone) so a known account stands out from the
 * net-new majority. (The Status column also surfaces this as a "Customer" pill;
 * this column answers the explicit Y/N the operator asked for.)
 */
export function ExistingCustomerCell({ lead }: { lead: SalesOrgLeadCard }): JSX.Element {
  const isCustomer = Boolean(lead.converted_at);
  return (
    <Box
      component="span"
      sx={{
        fontSize: 12,
        fontWeight: isCustomer ? 700 : 500,
        color: isCustomer ? "success.main" : "text.disabled",
      }}
    >
      {isCustomer ? "Yes" : "No"}
    </Box>
  );
}

/**
 * "RFP" — does this lead hang off a dated procurement notice, and when are bids
 * due? Derived from the generic deadline: a `deadline_kind === "rfp"` (with the
 * canonical `deadline` date) or the legacy `rfp_deadline` compat column. Shows
 * "Yes · YYYY-MM-DD" (the response/submission date) when known, "Yes" when an
 * RFP is flagged without a captured date, else "No".
 *
 * "100% proof" gate (2026-06-16): "Yes" flags that the org hangs off a
 * procurement notice; the DATE below it is shown ONLY when it's been verified
 * (`deadline_verified === true`) against the cited source. When the date hasn't
 * been confirmed yet, it's still shown (transparency — the operator sees what the
 * agent captured) but on its own row, muted, with an explicit "unverified"
 * marker beneath it, so it reads as "claimed, not confirmed" rather than an
 * assertion of fact. The tooltip carries the reason + that a background sweep
 * keeps re-checking it. (The lead detail's DeadlineProofNote says the same.)
 */
export function RfpCell({ lead }: { lead: SalesOrgLeadCard }): JSX.Element {
  const rfpDate =
    (lead.deadline_kind === "rfp" ? lead.deadline : null) ?? lead.rfp_deadline ?? null;
  const isRfp = lead.deadline_kind === "rfp" || Boolean(lead.rfp_deadline);
  if (!isRfp) {
    return (
      <Box component="span" sx={{ fontSize: 12, color: "text.disabled" }}>
        No
      </Box>
    );
  }
  const dateVerified = lead.deadline_verified === true;
  const unverifiedTitle =
    (lead.deadline_proof ? `${lead.deadline_proof}. ` : "") +
    "The agent captured this date but it isn't verified against the cited source yet. " +
    "A background check re-verifies it each cycle; sources the agent can't read stay unverified.";
  return (
    <Box sx={{ fontSize: 12, color: "text.primary", lineHeight: 1.3 }}>
      {/* "Yes" on its own row — the date/marker always sit BELOW it (the cell is
          narrow, so nothing is ever jammed onto the "Yes" line). */}
      <Box sx={{ fontWeight: 700, color: "warning.dark" }}>Yes</Box>
      {rfpDate ? (
        <>
          <Box
            sx={{
              fontSize: 11,
              whiteSpace: "nowrap",
              color: dateVerified ? "text.secondary" : "text.disabled",
              fontStyle: dateVerified ? "normal" : "italic",
            }}
          >
            {rfpDate}
          </Box>
          {!dateVerified ? (
            <Box
              title={unverifiedTitle}
              sx={{ fontSize: 10, fontWeight: 700, color: "warning.main", whiteSpace: "nowrap" }}
            >
              unverified
            </Box>
          ) : null}
        </>
      ) : null}
    </Box>
  );
}

/**
 * A placeholder operator column (Project timeline / Project budget / Lead
 * score). The agent does not emit these yet, so the value is typically absent —
 * the cell renders a quiet em-dash rather than a blank cell. When the field
 * later carries data it shows verbatim, no UI change. (The em-dash mirrors the
 * Created/Last-signal "missing" treatment so the table reads consistently.)
 */
export function PlaceholderCell({ value }: { value: string | null | undefined }): JSX.Element {
  const text = value && value.trim() ? value : "—";
  return (
    <Box
      component="span"
      title={text === "—" ? "" : text}
      sx={{
        display: "block",
        fontSize: 12,
        color: text === "—" ? "text.disabled" : "text.primary",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </Box>
  );
}

/**
 * Lead-quality VERDICT marker — the independent judge's plain-language
 * assessment of how good the lead is: **Confirmed** (very good) / **Worth a
 * look** (flagged for a human) / **Quality flag** (concerns raised) / **Review
 * pending** (not yet scored). This is the answer to "is this lead worth my
 * time?", distinct from the pipeline `StatusPill` ("where is it in the
 * lifecycle?"): an Archived lead reads "Quality flag", a kept lead reads
 * "Confirmed". Reuses the SAME `verdictMeta(eval_verdict)` + `toneChipSx` the
 * lead-detail hero badge uses, so the two surfaces never drift. The blurb is
 * the hover tooltip.
 */
export function QualityPill({ lead }: { lead: SalesOrgLeadCard }): JSX.Element {
  const meta = verdictMeta(lead.eval_verdict);
  return (
    <Box
      component="span"
      title={meta.blurb}
      sx={[
        {
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          maxWidth: "100%",
          borderRadius: 999,
          border: 1,
          px: 1,
          py: 0.25,
          fontSize: 11,
          fontWeight: 600,
          lineHeight: 1.3,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        toneChipSx(meta.tone),
      ]}
    >
      <Box component="span" aria-hidden sx={{ fontSize: 12 }}>{meta.icon}</Box>
      {meta.label}
    </Box>
  );
}

/**
 * Pipeline-stage marker. The headline of the Leads "All" tab, where rows
 * span every status. A confirmed customer conversion (`converted_at`)
 * trumps the raw pipeline status — it's the strongest possible outcome.
 */
export function StatusPill({ lead }: { lead: SalesOrgLeadCard }): JSX.Element {
  const meta = statusMeta(lead);
  return (
    <Box
      component="span"
      title={meta.title}
      sx={[
        {
          display: "inline-flex",
          alignItems: "center",
          borderRadius: 999,
          px: 1,
          py: 0.25,
          fontSize: 10,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          whiteSpace: "nowrap",
        },
        meta.sx,
      ]}
    >
      {meta.label}
    </Box>
  );
}

export function statusMeta(lead: SalesOrgLeadCard): {
  label: string;
  title: string;
  sx: SxStyle;
} {
  // Solid, saturated pills so each operator bucket reads at a glance: green =
  // New, blue = Qualified, navy = Qualified-in-CRM (the `exported` stage),
  // red/grey = Rejected (`denied` / agent-archived `dismissed`), lime =
  // Customer (converted, trumps all). Palette tokens (+ their contrastText) so
  // it tracks the theme — never a raw hex.
  const solid = (
    key: "success" | "info" | "warning" | "error" | "primary" | "secondary",
  ): SxStyle => ({ bgcolor: `${key}.main`, color: `${key}.contrastText` });
  const grey: SxStyle = { bgcolor: "grey.500", color: "common.white" };

  // A confirmed customer conversion is the strongest outcome — show it
  // regardless of the raw pipeline status.
  if (lead.converted_at) {
    return { label: "Customer", title: "Converted to a CRM customer", sx: solid("secondary") };
  }
  switch (lead.status as SalesOrgLeadStatus) {
    case "new":
      return { label: "New", title: "New — awaiting review", sx: solid("success") };
    case "qualified":
      return { label: "Qualified", title: "Qualified", sx: solid("info") };
    case "denied":
      return { label: "Rejected", title: "Rejected — no further action", sx: solid("error") };
    case "dismissed":
      // Agent-archived junk reads "Rejected" too — both are "no further action".
      return { label: "Rejected", title: "Rejected (archived by the agent)", sx: grey };
    case "contacted":
      // Retired stage — kept rendering for historical rows (the agent never sets
      // it and the operator can no longer move a lead here).
      return { label: "Contacted", title: "Contacted (legacy stage)", sx: solid("warning") };
    case "exported":
      // "Qualified" (renamed from "Prospect" 2026-06-09): the lead has been
      // qualified INTO the CRM as a prospect — its done-state on this list.
      return { label: "Qualified", title: "Qualified into the CRM as a prospect", sx: solid("primary") };
    default:
      return { label: String(lead.status), title: String(lead.status), sx: grey };
  }
}
